import React, {useMemo} from 'react';
import {Spinner, SpinnerSize, Text, TooltipHost, useTheme} from '@fluentui/react';
import {IWorkspaceItem} from '../../models/Workspace';
import {useGetProductBundleItemsQuery} from '../../store/endpoints/ProductBundleEndpoints';
import {useGetAllVendorSettingsQuery} from '../../store/Api';
import {renderVendorNameFromAllSettings} from '../../logic/Product';

interface IBundleSourceSummaryProps {
    item: IWorkspaceItem;
}

const RULE_LABELS: {[key: string]: string} = {
    LowestInStock: 'Lowest in stock',
    LowestPrice: 'Lowest price',
};

/**
 * The Source cell for a bundle line: a count-per-source summary of what the bundle's items are set
 * to ("2 × Lowest in stock", "1 × Ingram"), with the per-item breakdown on hover. Saved-search items
 * have no single product and are skipped; items with no source show as Default.
 */
const BundleSourceSummary: React.FC<IBundleSourceSummaryProps> = ({item}) => {
    const theme = useTheme();
    const items = useGetProductBundleItemsQuery(item.sourceEntityId ?? '', {skip: !item.sourceEntityId});
    const vendorSettings = useGetAllVendorSettingsQuery();

    const summary = useMemo(() => {
        const list = (items.data?.items ?? []).filter((bi) => !bi.savedSearchId);
        const label = (source?: string) => !source ? 'Default' : (RULE_LABELS[source] ?? renderVendorNameFromAllSettings(source, vendorSettings.data));
        const groups = new Map<string, {label: string; names: string[]}>();
        for (const bi of list) {
            const key = bi.details?.source ?? '';
            const entry = groups.get(key) ?? {label: label(bi.details?.source), names: []};
            entry.names.push(bi.sourceItem?.name ?? bi.productId);
            groups.set(key, entry);
        }
        return [...groups.values()].sort((a, b) => b.names.length - a.names.length);
    }, [items.data, vendorSettings.data]);

    if (!item.sourceEntityId) return <div>Bundle</div>;
    if (items.isLoading) return <Spinner size={SpinnerSize.xSmall} />;
    if (summary.length === 0) return <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>No items</Text>;

    const detail = (
        <div>
            {summary.map((group) => (
                <div key={group.label} style={{marginBottom: 4}}>
                    <Text variant='small' block styles={{root: {fontWeight: 600}}}>{group.label}</Text>
                    {group.names.map((name, i) => <Text key={i} variant='small' block styles={{root: {color: theme.palette.neutralSecondary}}}>{name}</Text>)}
                </div>
            ))}
        </div>
    );

    return (
        <TooltipHost content={detail}>
            <div style={{lineHeight: '16px', cursor: 'help'}}>
                {summary.map((group) => (
                    <Text key={group.label} variant='small' block styles={{root: {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}}>
                        <span style={{color: theme.palette.neutralSecondary}}>{group.names.length} ×</span> {group.label}
                    </Text>
                ))}
            </div>
        </TooltipHost>
    );
};

export default BundleSourceSummary;
